<script lang="ts" setup>
  import type { Header } from '@tanstack/vue-table';
  import { computed, inject, useCssModule } from 'vue';

  import type { SortValueDeserialized } from '../DataView/DataView.vue';
  import { DATA_VIEW_INJECTION } from '../DataView/DataView.vue';
  import Icon from '../Icon/Icon.vue';
  import { TABLE_INJECTION } from '../Table/Table.vue';

  export interface TableHeaderCellProps {
    /**
     * A unique value correlated to the data in this header's column (without a leading hyphen/dash).
     * For example: `'product_name'` (with no leading dash/hyphen).
     */
    sortId?: SortValueDeserialized['id'];

    /**
     * TanStack header instance passed when Table is in schema-driven mode.
     * When provided, sorting is controlled by TanStack instead of DataView.
     * Typed as any so DataTable can pass Header<TData, unknown> without a type assertion.
     */
    schemaDrivenHeader?: Header<any, any>;
  }

  const props = withDefaults(defineProps<TableHeaderCellProps>(), {
    sortId: '',
    schemaDrivenHeader: undefined,
  });
  const classes = useCssModule();

  const tableInjection = inject(TABLE_INJECTION.key);
  if (!tableInjection) {
    throw new Error('TableHeaderCell must be used within a Table component');
  }

  const { density, hasActions } = tableInjection;

  const hasSchemaDrivenHeader = computed(() => !!props.schemaDrivenHeader);

  const isPinnedHeader = computed(
    () => hasSchemaDrivenHeader.value && !!props.schemaDrivenHeader!.column.getIsPinned(),
  );

  // #region sorting
  const { currentSortId, currentSortOrder, updateCurrentSort } = inject(
    DATA_VIEW_INJECTION.key,
    DATA_VIEW_INJECTION.defaults,
  );

  const isSortable = computed(() => {
    if (hasSchemaDrivenHeader.value) {
      return props.schemaDrivenHeader!.column.getCanSort();
    }
    return !!props.sortId;
  });

  const isActiveSort = computed(() => {
    if (hasSchemaDrivenHeader.value) {
      return !!props.schemaDrivenHeader!.column.getIsSorted();
    }
    return currentSortId.value === props.sortId;
  });

  const sortDirection = computed<'asc' | 'desc' | false>(() => {
    if (hasSchemaDrivenHeader.value) {
      return props.schemaDrivenHeader!.column.getIsSorted();
    }
    if (isActiveSort.value && currentSortOrder.value) {
      return currentSortOrder.value;
    }
    return false;
  });

  function onRootClick(event: MouseEvent) {
    if (hasSchemaDrivenHeader.value) {
      props.schemaDrivenHeader!.column.getToggleSortingHandler()?.(event);
      return;
    }

    if (isSortable.value && typeof updateCurrentSort === 'function') {
      updateCurrentSort(props.sortId, { shouldEmit: true });
    }
  }
  // #endregion sorting
</script>

<template>
  <th
    class="stash-table-header-cell border-b border-r border-ice-200 p-3 text-xs font-medium text-ice-900"
    :class="[
      classes.root,
      {
        'bg-white': !isActiveSort,
        'stash-table-header-cell--sorted bg-blue-100': isActiveSort,
        [classes['root--density-compact']]: density === 'compact',
        [classes['root--density-comfortable']]: density === 'comfortable',
        [classes['has-actions']]: hasActions,
        [classes['pinned-sticky']]: isPinnedHeader,
        'cursor-pointer': isSortable,
      },
    ]"
    data-test="stash-table-header-cell"
    @click="onRootClick"
  >
    <div class="flex justify-between" :class="classes['content-wrapper']">
      <!-- @slot default -->
      <slot></slot>
      <div v-if="isSortable" class="relative h-4 w-3">
        <Icon
          v-if="!isActiveSort || sortDirection === 'asc'"
          class="absolute text-ice-700"
          :class="[classes.caret, classes['caret-up']]"
          data-test="icon|caret-up"
          name="caret-up"
          size="dense"
        />
        <Icon
          v-if="!isActiveSort || sortDirection === 'desc'"
          class="absolute text-ice-700"
          :class="[classes.caret, classes['caret-down']]"
          data-test="icon|caret-down"
          name="caret-down"
          size="dense"
        />
      </div>
    </div>
  </th>
</template>

<style module>
  @reference "../../../styles/main.css";

  .root {
    border-left: none;
    border-top: none;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .root:last-of-type {
    border-right: 0;
  }

  .pinned-sticky {
    z-index: 2;
  }

  .has-actions:last-of-type .content-wrapper {
    justify-content: flex-end !important;
  }

  .caret-up {
    top: -5px;
  }

  .caret-down {
    top: 2px;
  }

  :global(.stash-table-header-cell--sorted) .caret {
    top: -2px; /* vertically center the caret icon */
  }

  @media (width >= theme(--breakpoint-lg)) {
    .root--density-comfortable:last-of-type {
      padding-right: 24px;
    }

    .root--density-comfortable:first-of-type:not(
        :global(.stash-table-header-row__selection-cell),
        :global(.stash-table-row__selection-cell)
      ) {
      padding-left: 24px;
    }
  }
</style>
